import styled, { CSSObject } from '@emotion/styled';
import { FC, PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useNavigate } from 'react-router-dom';

import Breadcrumbs, { BreadcrumbsProps } from 'app/components/complex/Breadcrumbs';
import { BoxProps } from 'app/components/v2/components/Box';
import styles from 'app/components/v2/constants/styles';
import { getScrollPosition, useDidMount } from 'app/components/v2/hooks';
import { generateTitle } from 'app/constants/routes';
import { useGeneralDispatch, useGeneralState } from 'app/state/GeneralContext/hooks';
import { useNavigationDispatch } from 'app/state/NavigationContext/hooks';
import { parseSearch } from 'app/utils/lib/string';

import SkeletonLoader, {
  AnimatedWrapper,
  skeletonTransitionSpeedInMs,
  SkeletonType,
} from './SkeletonLoader';
import Container from '../Container';

// Local Typings
interface SearchParams {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  without_animation?: string;
}
interface PageRestriction {
  redirectTo?: string;
  restrictedCondition: boolean;
}
export interface PageProps extends WrapperProps {
  breadcrumbsContainerMaxWidth?: BoxProps['maxWidth'];
  breadcrumbsProps?: BreadcrumbsProps;
  loading: boolean;
  restrictions?: PageRestriction[];
  skeletonType?: SkeletonType;
  title: string;
  withScrollListener?: boolean;
}
interface WrapperProps {
  backgroundImage?: string;
  backgroundImageLoader?: string;
  fullWidthMobile?: boolean;
  isFullScreen?: boolean;
  noPaddingBottom?: boolean;
  noPaddingX?: boolean;
  noPaddingY?: boolean;
  overflowY?: CSSObject['overflowY'];
}

const getMobilePadding = ({
  fullWidthMobile,
  noPaddingBottom,
  noPaddingX,
  noPaddingY,
}: WrapperProps) => {
  if (fullWidthMobile) {
    return `0 0 ${styles.padding.xxl}`;
  }

  const paddingBottom = (noPaddingBottom || noPaddingY) ? styles.padding.none : styles.padding.xxl;
  const paddingTop = noPaddingY ? styles.padding.none : styles.padding.md;
  const paddingX = noPaddingX ? styles.padding.none : styles.padding.md;

  return `${paddingTop} ${paddingX} ${paddingBottom}`;
};

// Local Variables
export const pagePaddingBottom = 100;

const getPaddingX = (props: WrapperProps) =>
  props.noPaddingX ? '0' : styles.padding.lg;

const getPaddingY = (props: WrapperProps) =>
  props.noPaddingY ? '0' : styles.padding.xxxxl;

const Wrapper = styled.div<WrapperProps & { isVisible: boolean }>(props => ({
  backgroundImage: props.backgroundImage ? `url(${props.backgroundImage})` : undefined,
  backgroundPosition: 'center',
  backgroundSize: 'cover',
  bottom: props.isFullScreen ? 0 : undefined,
  flex: 1,
  height: '100%',
  left: props.isFullScreen ? 0 : undefined,
  overflowY: props.overflowY,
  padding: isMobile
    ? getMobilePadding(props)
    : `${getPaddingY(props)} ${getPaddingX(props)}`,
  paddingBottom: props.noPaddingBottom
    ? styles.padding.none
    : pagePaddingBottom, // for intercom
  position: props.isFullScreen ? 'fixed' : undefined,
  right: props.isFullScreen ? 0 : undefined,
  top: props.isFullScreen ? 0 : undefined,
  visibility: props.isVisible ? 'visible' : 'hidden',
  width: '100%',
  zIndex: props.isFullScreen ? styles.zIndex.drawer : undefined,
}));
const Relative = styled.div({
  height: '100%',
  position: 'relative',
  width: '100%',
});
const Absolute = styled.div({
  bottom: 0,
  left: 0,
  position: 'absolute',
  right: 0,
  top: 0,
});
export const pageWrapperClassName = 'page-wrapper';
export const scrollXVarName = '--scroll-x';
export const scrollYVarName = '--scroll-y';

// Component Definition
const Page: FC<PropsWithChildren<PageProps>> = props => {
  const navigate = useNavigate();
  const generalDispatch = useGeneralDispatch();
  const navigationDispatch = useNavigationDispatch();

  const [isLoading, setIsLoading] = useState(props.loading);
  const [isUrlLoaded, setIsUrlLoaded] = useState(false);
  const restriction = props.restrictions
    ? props.restrictions.find(({ restrictedCondition }) => restrictedCondition)
    : null;
  const { without_animation: withoutAnimation } = parseSearch(window.location.search) as SearchParams;
  const {
    currentDevice,
    isSideNavOpen,
  } = useGeneralState();
  const didMount = useDidMount();

  const setPageTitle = useCallback(() => {
    const pageTitle = generateTitle(props.title);
    document.title = pageTitle;
  },
  [props.title]);

  useEffect(() => {
    if (props.withScrollListener) {
      const pageWrapper = document.querySelector(`.${pageWrapperClassName}`);

      // set css vars
      document.documentElement.style.setProperty(scrollXVarName,
        '0');
      document.documentElement.style.setProperty(scrollYVarName,
        '0');

      const scrollListener = () => {
        const {
          x,
          y,
        } = getScrollPosition(pageWrapper as HTMLElement);

        // set css vars
        document.documentElement.style.setProperty(scrollXVarName,
          x.toString());
        document.documentElement.style.setProperty(scrollYVarName,
          y.toString());
      };

      pageWrapper?.addEventListener(
        'scroll',
        scrollListener,
        false,
      );

      return () => {
        pageWrapper?.removeEventListener(
          'scroll',
          scrollListener,
          false,
        );
      };
    }
  },
  [props.withScrollListener]);

  useEffect(() => {
    setPageTitle();
    navigationDispatch({
      payload: { path: props.title },
      type: 'setPath',
    });
  },
  [navigationDispatch, props.title, setPageTitle]);

  useEffect(() => {
    let timer: number;

    if (!props.loading) {
      timer = window.setTimeout(() => {
        setIsLoading(false);
      },
      skeletonTransitionSpeedInMs * 2);
    }

    return () => {
      window.clearTimeout(timer);
    };
  },
  [props.loading]);

  useEffect(() => {
    if (props.backgroundImage) {
      const image = new Image();
      image.src = props.backgroundImage;
      image.onload = () => {
        setIsUrlLoaded(true);
      };
    }
  },
  [props.backgroundImage]);

  useEffect(() => {
    if (currentDevice !== 'desktop' && isSideNavOpen) {
      generalDispatch({
        type: 'toggleSideNav',
      });
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);

  useEffect(() => {
    if (restriction) {
      navigate(restriction.redirectTo ?? '/',
        { replace: true });
    }
  },
  [navigate, restriction]);

  return restriction
    ? null
    : (
      <Wrapper
        backgroundImage={props.backgroundImageLoader && !isUrlLoaded
          ? props.backgroundImageLoader
          : props.backgroundImage}
        className={pageWrapperClassName}
        fullWidthMobile={props.fullWidthMobile}
        isFullScreen={props.isFullScreen}
        isVisible={didMount || !!withoutAnimation}
        noPaddingBottom={props.noPaddingBottom}
        noPaddingX={props.noPaddingX}
        noPaddingY={props.noPaddingY}
        overflowY={props.overflowY}
      >
        {props.breadcrumbsProps && (
          <Container maxWidth={props.breadcrumbsContainerMaxWidth}>
            <Breadcrumbs
              {...props.breadcrumbsProps}
              marginBottom={8}
            />
          </Container>
        )}

        <Relative>
          {isLoading && (
            <Absolute>
              <AnimatedWrapper opacity={props.loading ? 0.5 : 0}>
                <SkeletonLoader skeletonType={props.skeletonType} />
              </AnimatedWrapper>
            </Absolute>
          )}

          <AnimatedWrapper opacity={(!withoutAnimation && props.loading) ? 0 : 1}>

            {props.children}
          </AnimatedWrapper>
        </Relative>
      </Wrapper>
    );
};

export default Page;
